// Image preloader utility for performance optimization
class ImagePreloader {
  constructor() {
    this.cache = new Map();
    this.preloadQueue = [];
    this.isPreloading = false;
  }

  // Preload critical images immediately
  preloadCriticalImages() {
    const criticalImages = [
      '/media/logo.png',
      '/media/hand_ibubbleIQ_bobatea.webp',
      // Add first few product images that are likely to be visible
      '/media/tea/black_tea.png',
      '/media/0-creamer.png',
      '/media/FLAVORED POWDER/1kg Chocolate.png',
    ];

    criticalImages.forEach(src => this.preloadImage(process.env.PUBLIC_URL + src));
  }

  // Preload image and return promise
  preloadImage(src) {
    if (this.cache.has(src)) {
      return Promise.resolve(this.cache.get(src));
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.cache.set(src, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });

    this.cache.set(src, promise);
    return promise;
  }

  // Preload images with priority queue
  preloadWithPriority(images, priority = 'low') {
    const preloadPromises = images.map(src => {
      if (priority === 'high') {
        return this.preloadImage(src);
      } else {
        // Use requestIdleCallback for low priority preloading
        return new Promise(resolve => {
          if (window.requestIdleCallback) {
            requestIdleCallback(() => {
              this.preloadImage(src).then(resolve).catch(resolve);
            });
          } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(() => {
              this.preloadImage(src).then(resolve).catch(resolve);
            }, 100);
          }
        });
      }
    });

    return Promise.allSettled(preloadPromises);
  }

  // Generate optimized image URLs with size parameters
  getOptimizedImageUrl(src, width = null, height = null) {
    // For external URLs, we can't optimize them directly
    if (src.startsWith('http')) {
      return src;
    }

    // For local images, we could implement WebP conversion or size optimization
    // For now, just return the original URL
    return src;
  }

  // Lazy load images with intersection observer
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              observer.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '50px 0px', // Start loading 50px before the image comes into view
      });

      return imageObserver;
    }
    return null;
  }
}

export const imagePreloader = new ImagePreloader();
export default imagePreloader;